import PropTypes from "prop-types"
import React, { useState } from "react"
import Image from "components/ui/Image"

/**
 * ImageCarousel - A carousel/slideshow component for multiple images
 *
 * Usage Examples:
 *
 * 1. Array format (JSX):
 * <ImageCarousel
 *   images={[
 *     { src: "/images/photo1.jpg", caption: "First photo" },
 *     { src: "/images/photo2.jpg", caption: "Second photo" },
 *   ]}
 * />
 *
 * 2. MDX-friendly format (up to 10 images):
 * <imagecarousel
 *   image1="./images/photo1.jpg" caption1="First photo"
 *   image2="./images/photo2.jpg" caption2="Second photo"
 *   image3="./images/photo3.jpg" caption3="Third photo"
 * />
 *
 * 3. With title:
 * <imagecarousel
 *   title="FOSDEM 2026 Gallery"
 *   image1="./images/photo1.jpg" caption1="First photo"
 *   image2="./images/photo2.jpg" caption2="Second photo"
 * />
 */
function ImageCarousel({
  images,
  title,
  image1,
  image2,
  image3,
  image4,
  image5,
  image6,
  image7,
  image8,
  image9,
  image10,
  caption1,
  caption2,
  caption3,
  caption4,
  caption5,
  caption6,
  caption7,
  caption8,
  caption9,
  caption10,
}) {
  const [currentIndex, setCurrentIndex] = useState(0)

  // Build image array from props
  let imageArray = []

  if (images && Array.isArray(images)) {
    imageArray = images
  } else {
    // MDX-friendly format: image1, image2, etc.
    const mdxImages = [
      { src: image1, caption: caption1 },
      { src: image2, caption: caption2 },
      { src: image3, caption: caption3 },
      { src: image4, caption: caption4 },
      { src: image5, caption: caption5 },
      { src: image6, caption: caption6 },
      { src: image7, caption: caption7 },
      { src: image8, caption: caption8 },
      { src: image9, caption: caption9 },
      { src: image10, caption: caption10 },
    ]
    imageArray = mdxImages.filter((img) => img.src)
  }

  if (!imageArray.length) return null

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? imageArray.length - 1 : prev - 1))
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === imageArray.length - 1 ? 0 : prev + 1))
  }

  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      goToPrevious()
    } else if (e.key === "ArrowRight") {
      goToNext()
    }
  }

  const currentImage = imageArray[currentIndex]

  return (
    <div
      role="region"
      aria-label={title || "Image carousel"}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: Carousel needs focus for keyboard navigation
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        margin: "0",
        padding: "1.5rem",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        borderRadius: "8px",
        backgroundColor: "#fafafa",
        outline: "none",
      }}
    >
      {title && (
        <h3
          style={{
            margin: "0 0 1rem 0",
            fontSize: "1.25rem",
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          {title}
        </h3>
      )}

      {/* Image container */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "400px",
          overflow: "hidden",
        }}
      >
        {/* Previous button */}
        <button
          type="button"
          onClick={goToPrevious}
          aria-label="Previous image"
          style={{
            position: "absolute",
            left: "0.5rem",
            zIndex: 10,
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            border: "none",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            color: "white",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 0.2s",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.7)"
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.5)"
          }}
          onFocus={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.7)"
          }}
          onBlur={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.5)"
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Current image */}
        <div
          style={{
            textAlign: "center",
            padding: "0 3rem",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Image
            src={currentImage.src}
            alt={currentImage.caption || `Image ${currentIndex + 1}`}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              margin: "0 auto",
            }}
          />
        </div>

        {/* Next button */}
        <button
          type="button"
          onClick={goToNext}
          aria-label="Next image"
          style={{
            position: "absolute",
            right: "0.5rem",
            zIndex: 10,
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            border: "none",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            color: "white",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 0.2s",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.7)"
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.5)"
          }}
          onFocus={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.7)"
          }}
          onBlur={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.5)"
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* Caption */}
      <div
        style={{
          minHeight: "3rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "1rem 0 0.5rem 0",
        }}
      >
        {currentImage.caption && (
          <p
            style={{
              margin: 0,
              textAlign: "center",
              fontStyle: "italic",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            {currentImage.caption}
          </p>
        )}
      </div>

      {/* Dots indicator */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "0.5rem",
          marginTop: "0.75rem",
        }}
      >
        {imageArray.map((_, index) => (
          <button
            key={`dot-${index}`}
            type="button"
            onClick={() => setCurrentIndex(index)}
            aria-label={`Go to image ${index + 1}`}
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              border: "none",
              backgroundColor: index === currentIndex ? "#333" : "#ccc",
              cursor: "pointer",
              padding: 0,
              transition: "background-color 0.2s",
            }}
          />
        ))}
      </div>

      {/* Counter */}
      <p
        style={{
          margin: "0.5rem 0 0 0",
          textAlign: "center",
          fontSize: "0.85rem",
          color: "#888",
        }}
      >
        {currentIndex + 1} / {imageArray.length}
      </p>
    </div>
  )
}

ImageCarousel.propTypes = {
  /** Array of images with src and optional caption */
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      caption: PropTypes.string,
    })
  ),
  /** Optional title above the carousel */
  title: PropTypes.string,
  /** MDX-friendly image props (image1 through image10) */
  image1: PropTypes.string,
  image2: PropTypes.string,
  image3: PropTypes.string,
  image4: PropTypes.string,
  image5: PropTypes.string,
  image6: PropTypes.string,
  image7: PropTypes.string,
  image8: PropTypes.string,
  image9: PropTypes.string,
  image10: PropTypes.string,
  /** MDX-friendly caption props (caption1 through caption10) */
  caption1: PropTypes.string,
  caption2: PropTypes.string,
  caption3: PropTypes.string,
  caption4: PropTypes.string,
  caption5: PropTypes.string,
  caption6: PropTypes.string,
  caption7: PropTypes.string,
  caption8: PropTypes.string,
  caption9: PropTypes.string,
  caption10: PropTypes.string,
}

export default ImageCarousel
